import React, { createContext, ReactElement, useEffect, useState } from 'react';
import { useIdentityContext } from '@design-stack-vista/identity-provider';
import {
  convertVariantAttributeRangeToSelectableValues,
  DesignSpecServiceResponse,
  getMpvId,
  getProductConfiguration,
  getProductName,
  getSpecificationItemsAsync,
  VariantAttribute,
} from '@vp/upload-and-print-validator';
import { getProductOptions } from 'src/services/msxService';
import { OptionsAndPrice } from 'src/services/UploadAndPrintService';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { currentCulture } from 'src/utils/cultureUtil';
import { noticeError } from 'src/utils/newRelicUtil';
import QueryStringUtil from 'src/utils/QueryStringUtil';
import { getTenant } from 'src/utils/tenantUtil';

export interface ProductInformation {
  productKey: string;
  productVersion: number;
  productName: string;
  mpvId: string;
  quantity: number;
  selectedOptions: ProductOptions;
  recommendedProductData?: OptionsAndPrice;
}

export interface ProductOptions {
  [key: string]: string;
}

export interface ProductContextProps {
  productInformation: ProductInformation;
  completedProductInformation: ProductInformation | null;
  setProductInformation: (newInfo: ProductInformation) => void;
  isProductReady: boolean;
  productOptionTranslations: VariantAttribute[] | null;
  completeProductOptions: ProductOptions;
  designSpecification: DesignSpecServiceResponse;
}

const getProductContextFromQueryParams = () => {
  const productKey = QueryStringUtil.getValueByKey('key') ?? '';
  const productVersionString = QueryStringUtil.getValueByKey('productVersion');
  const productVersion = productVersionString ? parseInt(productVersionString, 10) : 0;
  const quantityString = QueryStringUtil.getValueByKey('qty');
  const quantity = quantityString ? parseInt(quantityString, 10) : 0;
  const encodedSelectedOptions = QueryStringUtil.getValueByKey('selectedOptions');
  const selectedOptions = encodedSelectedOptions ? JSON.parse(decodeURIComponent(encodedSelectedOptions)) : {};

  return {
    productKey,
    productVersion,
    productName: '',
    mpvId: '',
    quantity,
    selectedOptions,
  };
};

export const ProductContext = createContext<ProductContextProps>({
  productInformation: getProductContextFromQueryParams(),
  completedProductInformation: null,
  setProductInformation: () => {
    return;
  },
  productOptionTranslations: null,
  isProductReady: false,
  completeProductOptions: {},
  designSpecification: { designViews: [], projectionId: '' },
});

export interface ProductContextProviderProps {
  children?: React.ReactNode;
}

export const ProductContextProvider = ({ children }: ProductContextProviderProps): ReactElement => {
  const [productInformation, setProductInformation] = useState(getProductContextFromQueryParams());
  const [fullProductInformation, setFullProductInformation] = useState<ProductInformation | null>(null);
  const [completedSelectedOptions, setCompletedSelectedOptions] = useState<ProductOptions | null>(null);
  const [productOptionTranslations, setProductOptionTranslations] = useState<VariantAttribute[] | null>(null);
  const { auth: vpAuth } = useIdentityContext();

  const [designSpecForValidationManager, setDesignSpecForValidationManager] = useState<DesignSpecServiceResponse>({
    designViews: [],
    projectionId: '',
  });

  useEffect(() => {
    let isStale = false;
    const setAdditionalProductInformation = async (): Promise<void> => {
      const productInfo = { ...productInformation };

      if (productInfo.productKey) {
        try {
          productInfo.productName = await getProductName(
            productInfo.productKey,
            currentCulture.toLowerCase(),
            getTenant(),
            getAuthHeader()
          );
        } catch (error) {
          noticeError(error, { message: 'Invalid response from MSX getting product name' });
        }

        try {
          productInfo.mpvId = await getMpvId(
            productInfo.productKey,
            currentCulture.toLowerCase(),
            getTenant(),
            getAuthHeader()
          );
        } catch (error) {
          noticeError(error, { message: 'Invalid response from MSX getting product mpvId' });
        }
      }

      if (isStale) {
        return;
      }

      setFullProductInformation(productInfo);
    };

    setAdditionalProductInformation();
    return () => {
      isStale = true;
    };
  }, [productInformation, setFullProductInformation]);

  useEffect(() => {
    let isStale = false;
    if (!vpAuth || !productInformation.productKey || !productInformation.productVersion) {
      return;
    }

    const fetchCompleteSelectedOptions = async (): Promise<void> => {
      try {
        const completedOptions = await getProductConfiguration(
          {
            productId: productInformation.productKey,
            version: productInformation.productVersion,
            productOptions: productInformation.selectedOptions,
            market: currentCulture.split('-')[1],
            tenant: getTenant(),
          },
          getAuthHeader()
        );

        if (isStale) {
          return;
        }

        setCompletedSelectedOptions(completedOptions);
      } catch (error) {
        noticeError(error, { message: 'Error from Ranch service getting product configuration' });
      }
    };

    const fetchProductOptionTranslations = async (): Promise<void> => {
      const allOptionsWithRange = await getProductOptions(
        productInformation.productKey,
        productInformation.productVersion
      );
      const allOptions = convertVariantAttributeRangeToSelectableValues(allOptionsWithRange);

      if (isStale) {
        return;
      }

      setProductOptionTranslations(allOptions);
    };

    fetchCompleteSelectedOptions();
    fetchProductOptionTranslations();
    return () => {
      isStale = true;
    };
  }, [productInformation, vpAuth]);

  useEffect(() => {
    const fetchDesignSpecification = async (): Promise<void> => {
      completedSelectedOptions &&
        getSpecificationItemsAsync(
          {
            productKey: productInformation.productKey,
            productVersion: productInformation.productVersion,
            optionSelections: completedSelectedOptions,
            culture: currentCulture.toLowerCase(),
            tenant: getTenant(),
          },
          { ...getAuthHeader() }
        ).then(result => {
          setDesignSpecForValidationManager(result);
        });
    };
    fetchDesignSpecification();
  }, [completedSelectedOptions, productInformation.productKey, productInformation.productVersion]);

  let isProductReady = false;

  let fullProduct = productInformation;
  if (fullProductInformation) {
    fullProduct = { ...fullProductInformation };
    isProductReady = true;
  }

  let completedProduct = null;

  if (completedSelectedOptions && isProductReady) {
    completedProduct = { ...fullProduct, selectedOptions: completedSelectedOptions };
  }

  return (
    <ProductContext.Provider
      value={{
        productInformation: fullProduct,
        completedProductInformation: completedProduct,
        setProductInformation,
        productOptionTranslations,
        isProductReady: isProductReady,
        completeProductOptions: completedSelectedOptions ?? {},
        designSpecification: designSpecForValidationManager,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
